"use client";

// Reads useReducedMotion and hands visual components to Client Components, so
// this file sits on the client side of the boundary.

import { SectionHeader, EnterpriseBanner, FeatureCard } from "@/components/ui/";
import { motion, useReducedMotion } from "@/lib/motion";
import { scrollRevealViewport } from "@/lib/motion-viewport";
import { STATS } from "@/lib/constants";
import FlagshipCard from "@/components/sections/products/shared/flagship-card";
import {
  GeoTargetVisual,
  RotationVisual,
} from "@/components/sections/products/shared/feature-visuals";
import IpPoolMock from "./ip-pool-mock";
import SuccessRateMock from "./success-rate-mock";

/* Two flagships over two supporting cards — the homepage's Why Proxidize card
   language, which this section had been left out of.

   The four features used to sit in an even grid of small tiles. Every layout
   that arrangement allows has the same problem: the visuals are 176px chip
   clusters built for a ~300px tile, so whatever the grid does, four equal cards
   each hold something small. Meanwhile this same page carries full product
   panels two sections down (the analytics dashboard, the session builder), and
   the features section read as the cheap one.

   So the fix is hierarchy plus substance, not another grid. The two headline
   claims — pool size and success rate — become flagship cards carrying real
   product panels. Geo-targeting and session modes stay compact supporting
   cards on the existing ambient visuals, which is exactly the size those
   visuals were designed for. */

const supportingFeatures = [
  {
    Visual: GeoTargetVisual,
    title: "Precision Geo-targeting",
    description:
      "Target by country and city. Route every request through the exact location your use case requires, down to the city level.",
  },
  {
    Visual: RotationVisual,
    title: "Rotating & Sticky Sessions",
    description:
      "Rotate IPs on every request or hold a session. Built for scraping, monitoring, and account workflows that need both options.",
  },
];

const containerVariants = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.08 } },
};

export default function ResidentialPricingFeatures() {
  const prefersReducedMotion = useReducedMotion();

  return (
    <section
      id="product-features"
      className="py-section border-border border-b"
      aria-labelledby="residential-features-heading"
    >
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="mb-14 max-w-2xl">
          <SectionHeader
            eyebrow="Why Proxidize"
            id="residential-features-heading"
            heading={
              <>
                Built for performance.
                <br />
                Priced for scale.
              </>
            }
            body="Here's what's included with every purchase."
          />
        </div>

        {/* The two flagship claims, each over a real product panel */}
        <motion.div
          variants={containerVariants}
          initial={prefersReducedMotion ? "visible" : "hidden"}
          whileInView="visible"
          viewport={scrollRevealViewport}
          className="grid gap-4 sm:gap-5 lg:grid-cols-2"
        >
          <FlagshipCard
            title={`${STATS.ips} Real Residential IPs`}
            description={`Premium residential IPs, ethically sourced from opt-in devices across ${STATS.countries} countries. Fully compliant and reserved for approved use cases only.`}
            reduced={prefersReducedMotion}
          >
            <IpPoolMock />
          </FlagshipCard>
          <FlagshipCard
            title="99.9%+ Platform Uptime"
            description="Residential proxies can improve access reliability for suitable workloads, but results depend on the target, location, session configuration, request behavior, and acceptance criteria."
            reduced={prefersReducedMotion}
          >
            <SuccessRateMock />
          </FlagshipCard>
        </motion.div>

        {/* Supporting pair — plain surfaces, no wave, so the flagships stay the
            focal point. Same shell as the homepage's four small cards. */}
        <motion.div
          variants={containerVariants}
          initial={prefersReducedMotion ? "visible" : "hidden"}
          whileInView="visible"
          viewport={scrollRevealViewport}
          className="mt-4 grid gap-4 sm:mt-5 sm:grid-cols-2 sm:gap-5"
        >
          {supportingFeatures.map((feature) => (
            <FeatureCard
              key={feature.title}
              Visual={feature.Visual}
              title={feature.title}
              description={feature.description}
            />
          ))}
        </motion.div>

        <div className="mt-10">
          <EnterpriseBanner description="Custom rotation rules, priority support, higher volume limits, and pricing starting at $0.5/GB." />
        </div>
      </div>
    </section>
  );
}
